import React, { useState, useCallback } from 'react';
import {
  ScrollView,
  RefreshControl,
  ViewStyle,
  ScrollViewProps,
  RefreshControlProps,
  Platform,
  View,
  Text,
} from 'react-native';

interface PullToRefreshWrapperProps extends ScrollViewProps {
  onRefresh: () => Promise<void> | void;
  refreshing?: boolean;
  refreshControlProps?: Partial<RefreshControlProps>;
  children: React.ReactNode;
  style?: ViewStyle;
  contentContainerStyle?: ViewStyle;
  pullText?: string;
  refreshingText?: string;
}

/**
 * A reusable wrapper component that adds pull-to-refresh functionality to ScrollView.
 *
 * @param onRefresh - Callback function that will be called when user pulls to refresh
 * @param refreshing - Optional boolean to control the refresh state externally
 * @param refreshControlProps - Optional props to customize the RefreshControl
 * @param children - The content to be wrapped
 * @param style - Style for the ScrollView
 * @param contentContainerStyle - Style for the ScrollView content container
 * @param pullText - Text to show when pulling down (default: "Pull down to refresh")
 * @param refreshingText - Text to show when refreshing (default: "Refreshing...")
 * @param ...scrollViewProps - All other ScrollView props are passed through
 */
export const PullToRefreshWrapper: React.FC<PullToRefreshWrapperProps> = ({
  onRefresh,
  refreshing: externalRefreshing,
  refreshControlProps = {},
  children,
  style,
  contentContainerStyle,
  pullText = 'Pull down to refresh',
  refreshingText = 'Refreshing...',
  ...scrollViewProps
}) => {
  const [internalRefreshing, setInternalRefreshing] = useState(false);

  // Use external refreshing state if provided, otherwise use internal state
  const isRefreshing =
    externalRefreshing !== undefined ? externalRefreshing : internalRefreshing;

  const handleRefresh = useCallback(async () => {
    // console.log('PullToRefreshWrapper: handleRefresh called');
    if (isRefreshing) {
      // console.log('PullToRefreshWrapper: Already refreshing, skipping');
      return; // Prevent multiple simultaneous refreshes
    }

    // console.log('PullToRefreshWrapper: Starting refresh');
    setInternalRefreshing(true);

    try {
      await onRefresh();
      // console.log('PullToRefreshWrapper: Refresh completed successfully');
    } catch (error) {
      // console.error('PullToRefreshWrapper: Error during refresh:', error);
    } finally {
      // console.log('PullToRefreshWrapper: Setting refreshing to false');
      setInternalRefreshing(false);
    }
  }, [onRefresh, isRefreshing]);

  const refreshControl = (
    <RefreshControl
      refreshing={isRefreshing}
      onRefresh={handleRefresh}
      tintColor="#ffffff" // White color for dark theme
      colors={['#ffffff']} // Android
      progressBackgroundColor="#1A1D21" // Dark background
      title={
        Platform.OS === 'ios'
          ? isRefreshing
            ? refreshingText
            : pullText
          : undefined
      } // iOS only
      titleColor="#ffffff" // White text color
      {...refreshControlProps}
    />
  );

  // console.log('PullToRefreshWrapper: Creating refresh control with:', {
  //   isRefreshing,
  //   pullText,
  //   refreshingText,
  //   platform: Platform.OS,
  // });

  return (
    <View style={{ flex: 1 }}>
      <ScrollView
        style={style}
        contentContainerStyle={contentContainerStyle}
        refreshControl={refreshControl}
        showsVerticalScrollIndicator={true}
        {...scrollViewProps}
      >
        {children}
      </ScrollView>

      {/* Custom text overlay for Android */}
      {Platform.OS === 'android' && isRefreshing && (
        <View
          style={{
            position: 'absolute',
            top: 20,
            left: 0,
            right: 0,
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <Text
            style={{
              color: '#ffffff',
              fontSize: 16,
              fontWeight: 'bold',
              backgroundColor: 'rgba(26, 29, 33, 0.8)',
              paddingHorizontal: 16,
              paddingVertical: 8,
              borderRadius: 20,
            }}
          >
            {refreshingText}
          </Text>
        </View>
      )}
    </View>
  );
};

export default PullToRefreshWrapper;
